import React, { useState, useEffect, useCallback, useRef } from "react";
import Modal from "react-modal";

// Force reload
const AdminSearchPreview: React.FC = () => {
  const [language, setLanguage] = useState<"da" | "en">("da");
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const resultsContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  // Store scroll positions for each tab
  const scrollPositions = useRef<{
    all: number,
    Affiliate: number,
    CardLinking: number
  }>({
    all: 0,
    Affiliate: 0,
    CardLinking: 0
  });
  const [queryBeforeFocus, setQueryBeforeFocus] = useState("");
  const shouldPreventBlurRevert = useRef(false);
  const [expandedSummaries, setExpandedSummaries] = useState<Set<number>>(new Set());
  const [expandedBrands, setExpandedBrands] = useState<Set<number>>(new Set());
  const [selectedResultJson, setSelectedResultJson] = useState<any>(null);
  const [isJsonModalOpen, setIsJsonModalOpen] = useState(false);
  const [partnerTypeFilter, setPartnerTypeFilter] = useState<"all" | "Affiliate" | "CardLinking">("all");
  const [facetCounts, setFacetCounts] = useState<{Affiliate: number, CardLinking: number, total: number}>({
    Affiliate: 0,
    CardLinking: 0,
    total: 0
  });

  // Store results and pagination state for each tab separately
  const [tabResults, setTabResults] = useState<{
    all: any[],
    Affiliate: any[],
    CardLinking: any[]
  }>({
    all: [],
    Affiliate: [],
    CardLinking: []
  });

  const [tabPages, setTabPages] = useState<{
    all: number,
    Affiliate: number,
    CardLinking: number
  }>({
    all: 1,
    Affiliate: 1,
    CardLinking: 1
  });

  const [tabHasMore, setTabHasMore] = useState<{
    all: boolean,
    Affiliate: boolean,
    CardLinking: boolean
  }>({
    all: true,
    Affiliate: true,
    CardLinking: true
  });

  // Fetch typeahead suggestions
  const fetchSuggestions = useCallback(async (searchQuery: string, lang: "da" | "en") => {
    if (!searchQuery.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    if (searchQuery.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      const typeaheadPreset = lang === "da"
        ? process.env.REACT_APP_TYPESENSE_PRESET_TYPEAHEAD_DA
        : process.env.REACT_APP_TYPESENSE_PRESET_TYPEAHEAD_EN;

      const multiSearchPayload = {
        searches: [
          {
            q: searchQuery.trim(),
            preset: typeaheadPreset || "",
            per_page: 5,
          }
        ]
      };

      const response = await fetch(
        `${process.env.REACT_APP_TYPESENSE_PROTOCOL}://${process.env.REACT_APP_TYPESENSE_HOST}:${process.env.REACT_APP_TYPESENSE_PORT}/multi_search`,
        {
          method: "POST",
          headers: {
            "X-TYPESENSE-API-KEY": process.env.REACT_APP_TYPESENSE_API_KEY || "",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(multiSearchPayload),
        }
      );

      const data = await response.json();
      const searchResult = data.results?.[0];

      // Extract highlighted completions from response
      const completions: string[] = [];
      const seen = new Set<string>(); // Track normalized versions to avoid duplicates

      if (searchResult?.hits) {
        searchResult.hits.forEach((hit: any) => {
          if (hit.highlights) {
            hit.highlights.forEach((highlight: any) => {
              if (highlight.snippets && Array.isArray(highlight.snippets)) {
                // Process each snippet in the snippets array
                highlight.snippets.forEach((snippet: string) => {
                  // Remove <mark> tags and get the full text
                  let cleanText = snippet.replace(/<\/?mark>/g, "");

                  // Replace hyphens and underscores with spaces
                  cleanText = cleanText.replace(/[-_]/g, " ");

                  // Capitalize first letter of each word
                  cleanText = cleanText
                    .split(" ")
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                    .join(" ");

                  // Check for duplicates using normalized lowercase version
                  const normalized = cleanText.toLowerCase();
                  if (cleanText && !seen.has(normalized)) {
                    seen.add(normalized);
                    completions.push(cleanText);
                  }
                });
              }
            });
          }
        });
      }

      setSuggestions(completions.slice(0, 5));
      setShowSuggestions(completions.length > 0);
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, []);

  // Debounced suggestion fetching
  useEffect(() => {
    // Only show suggestions if actively typing (at least 2 chars) AND input is focused
    if (isFocused && query.trim().length >= 2) {
      const timer = setTimeout(() => {
        fetchSuggestions(query, language);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [query, isFocused, language, fetchSuggestions]);

  // Fetch main search results - always performs 3 searches for all tabs
  const fetchSearchResults = useCallback(async (searchQuery: string, lang: "da" | "en", specificTab?: "all" | "Affiliate" | "CardLinking", isLoadMore: boolean = false, pageToFetch?: number) => {
    // Reset scroll position for new searches (not load more)
    if (!isLoadMore && resultsContainerRef.current) {
      resultsContainerRef.current.scrollTop = 0;
      // Also reset saved scroll positions for all tabs
      scrollPositions.current = {
        all: 0,
        Affiliate: 0,
        CardLinking: 0
      };
    }

    setLoading(true);
    try {
      // Use "*" for empty query to get all results
      const actualQuery = searchQuery.trim() === "" ? "*" : searchQuery;

      const searchPreset = lang === "da"
        ? process.env.REACT_APP_TYPESENSE_PRESET_DA
        : process.env.REACT_APP_TYPESENSE_PRESET_EN;

      // If it's a load more operation, only fetch for the specific tab
      if (isLoadMore && specificTab) {
        const pageNum = pageToFetch !== undefined ? pageToFetch : tabPages[specificTab];
        const singleSearch: any = {
          q: actualQuery,
          preset: searchPreset || "",
          per_page: 20,
          page: pageNum,
        };

        if (specificTab !== "all") {
          singleSearch.filter_by = `partner_type:=${specificTab}`;
        }

        const response = await fetch(
          `${process.env.REACT_APP_TYPESENSE_PROTOCOL}://${process.env.REACT_APP_TYPESENSE_HOST}:${process.env.REACT_APP_TYPESENSE_PORT}/multi_search`,
          {
            method: "POST",
            headers: {
              "X-TYPESENSE-API-KEY": process.env.REACT_APP_TYPESENSE_API_KEY || "",
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ searches: [singleSearch] }),
          }
        );

        const data = await response.json();
        const searchResult = data.results?.[0];

        if (searchResult?.hits) {
          setTabResults(prev => ({
            ...prev,
            [specificTab]: [...prev[specificTab], ...searchResult.hits.map((hit: any) => hit.document)]
          }));
          setTabHasMore(prev => ({
            ...prev,
            [specificTab]: searchResult.hits.length === 20
          }));
        } else {
          setTabHasMore(prev => ({
            ...prev,
            [specificTab]: false
          }));
        }
      } else {
        // Initial search or new query - fetch all three tabs
        const searches = [
          // 1. All results (unfiltered) - gets facet counts
          {
            q: actualQuery,
            preset: searchPreset || "",
            per_page: 20,
            page: 1,
          },
          // 2. Affiliate only
          {
            q: actualQuery,
            preset: searchPreset || "",
            per_page: 20,
            page: 1,
            filter_by: "partner_type:=Affiliate"
          },
          // 3. CardLinking only
          {
            q: actualQuery,
            preset: searchPreset || "",
            per_page: 20,
            page: 1,
            filter_by: "partner_type:=CardLinking"
          }
        ];

        const multiSearchPayload = {
          searches
        };

        const response = await fetch(
          `${process.env.REACT_APP_TYPESENSE_PROTOCOL}://${process.env.REACT_APP_TYPESENSE_HOST}:${process.env.REACT_APP_TYPESENSE_PORT}/multi_search`,
          {
            method: "POST",
            headers: {
              "X-TYPESENSE-API-KEY": process.env.REACT_APP_TYPESENSE_API_KEY || "",
              "Content-Type": "application/json",
            },
            body: JSON.stringify(multiSearchPayload),
          }
        );

        const data = await response.json();
        const [allResult, affiliateResult, cardLinkingResult] = data.results;

        // Update facet counts from the "all" result
        if (allResult?.facet_counts) {
          const partnerTypeFacet = allResult.facet_counts.find((f: any) => f.field_name === "partner_type");
          if (partnerTypeFacet) {
            const affiliateCount = partnerTypeFacet.counts.find((c: any) => c.value === "Affiliate")?.count || 0;
            const cardLinkingCount = partnerTypeFacet.counts.find((c: any) => c.value === "CardLinking")?.count || 0;
            setFacetCounts({
              Affiliate: affiliateCount,
              CardLinking: cardLinkingCount,
              total: affiliateCount + cardLinkingCount
            });
          }
        }

        // Update results for all tabs
        setTabResults({
          all: allResult?.hits?.map((hit: any) => hit.document) || [],
          Affiliate: affiliateResult?.hits?.map((hit: any) => hit.document) || [],
          CardLinking: cardLinkingResult?.hits?.map((hit: any) => hit.document) || []
        });

        // Update hasMore for all tabs
        setTabHasMore({
          all: (allResult?.hits?.length || 0) === 20,
          Affiliate: (affiliateResult?.hits?.length || 0) === 20,
          CardLinking: (cardLinkingResult?.hits?.length || 0) === 20
        });

        // Reset page numbers to 1 for all tabs
        setTabPages({
          all: 1,
          Affiliate: 1,
          CardLinking: 1
        });
      }
    } catch (error) {
      console.error("Error fetching search results:", error);
      if (!isLoadMore) {
        setTabResults({
          all: [],
          Affiliate: [],
          CardLinking: []
        });
        setTabHasMore({
          all: false,
          Affiliate: false,
          CardLinking: false
        });
      }
    } finally {
      setLoading(false);
    }
  }, [tabPages]);

  // Initial search with empty query on component mount
  useEffect(() => {
    fetchSearchResults("", language);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  // Remove automatic debounced search - only search on Enter or suggestion click

  // Load more results on scroll
  const handleScroll = useCallback(() => {
    if (!resultsContainerRef.current || loading || !tabHasMore[partnerTypeFilter]) return;

    const { scrollTop, scrollHeight, clientHeight } = resultsContainerRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 100) {
      const nextPage = tabPages[partnerTypeFilter] + 1;
      setTabPages(prev => ({
        ...prev,
        [partnerTypeFilter]: nextPage
      }));
      fetchSearchResults(query, language, partnerTypeFilter, true, nextPage);
    }
  }, [loading, tabHasMore, tabPages, query, language, partnerTypeFilter, fetchSearchResults]);

  useEffect(() => {
    const container = resultsContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  // Handle suggestion selection
  const handleSuggestionClick = (suggestion: string) => {
    // Prevent blur from reverting the query
    shouldPreventBlurRevert.current = true;

    setQuery(suggestion);
    setQueryBeforeFocus(suggestion);
    setShowSuggestions(false);

    // Trigger immediate search (will fetch all tabs)
    fetchSearchResults(suggestion, language);

    // Blur the input
    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  // Toggle summary expansion
  const toggleSummaryExpansion = (index: number) => {
    setExpandedSummaries(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // Toggle brands expansion
  const toggleBrandsExpansion = (index: number) => {
    setExpandedBrands(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // Handle JSON modal
  const openJsonModal = (result: any) => {
    setSelectedResultJson(result);
    setIsJsonModalOpen(true);
  };

  const closeJsonModal = () => {
    setIsJsonModalOpen(false);
    setSelectedResultJson(null);
  };

  // Handle Enter key press
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      // Prevent blur from reverting the query
      shouldPreventBlurRevert.current = true;

      setShowSuggestions(false);
      setIsFocused(false);
      // Unfocus the input
      e.currentTarget.blur();
      // Trigger search (with empty string if query is empty) - will fetch all tabs
      const searchQuery = query.trim() === "" ? "" : query;
      fetchSearchResults(searchQuery, language);
      // Update queryBeforeFocus to the current query after search
      setQueryBeforeFocus(searchQuery);
    }
  };

  // Handle language change
  const handleLanguageChange = (newLanguage: "da" | "en") => {
    setLanguage(newLanguage);
    setQuery("");
    setQueryBeforeFocus("");
    setShowSuggestions(false);
    setSuggestions([]);
    // Initial search will be triggered by the useEffect dependency on language
  };

  // Handle partner type filter change - just switch tabs, don't re-fetch
  const handleFilterChange = (filter: "all" | "Affiliate" | "CardLinking") => {
    // Save current scroll position before switching
    if (resultsContainerRef.current) {
      scrollPositions.current[partnerTypeFilter] = resultsContainerRef.current.scrollTop;
    }
    setPartnerTypeFilter(filter);
  };

  // Restore scroll position when tab changes
  useEffect(() => {
    if (resultsContainerRef.current) {
      resultsContainerRef.current.scrollTop = scrollPositions.current[partnerTypeFilter];
    }
  }, [partnerTypeFilter]);

  return (
    <div>
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="font-medium">Search Preview</h2>
            <p className="text-sm text-gray-600 mt-2">
              Preview the mobile app's search functionality with typeahead autocomplete
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Language:</label>
            <select
              value={language}
              onChange={(e) => handleLanguageChange(e.target.value as "da" | "en")}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="da">Danish</option>
              <option value="en">English</option>
            </select>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto h-[calc(100vh-200px)]">
        {/* Mobile-like container */}
        <div className="bg-gray-50 rounded-2xl shadow-lg overflow-hidden h-full flex flex-col">
          {/* Search header */}
          <div className="bg-primary p-4 flex-shrink-0">
            <div className="relative">
              {/* Custom search input with typeahead */}
              <div className="relative">
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onFocus={() => {
                    // Save the query before focus
                    setQueryBeforeFocus(query);
                    setIsFocused(true);
                    shouldPreventBlurRevert.current = false;
                    if (query.trim().length >= 2) {
                      setShowSuggestions(suggestions.length > 0);
                    }
                  }}
                  onBlur={() => {
                    setTimeout(() => {
                      setIsFocused(false);
                      setShowSuggestions(false);
                      // Revert to query before focus if user didn't click suggestion or press enter
                      if (!shouldPreventBlurRevert.current) {
                        setQuery(queryBeforeFocus);
                      }
                      shouldPreventBlurRevert.current = false;
                    }, 200);
                  }}
                  placeholder="Search for businesses..."
                  className="w-full px-4 py-3 pr-20 rounded-lg border-2 border-transparent focus:border-white focus:outline-none text-gray-800 placeholder-gray-400"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                  {query && (
                    <button
                      onClick={() => {
                        setQuery("");
                        setQueryBeforeFocus("");
                        setShowSuggestions(false);
                        // Trigger search with empty query to show all results
                        fetchSearchResults("", language);
                      }}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                      title="Clear search"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                  <svg
                    className="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
              </div>

              {/* Typeahead suggestions dropdown */}
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute z-10 w-full mt-2 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                  {suggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      onMouseDown={(e) => {
                        e.preventDefault(); // Prevent blur from firing
                        handleSuggestionClick(suggestion);
                      }}
                      className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                    >
                      <span className="text-gray-700 font-bold">{suggestion}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Filter buttons */}
          <div className="px-4 py-3 bg-white border-b border-gray-200 flex-shrink-0">
            <div className="flex gap-2">
              <button
                onClick={() => handleFilterChange("all")}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  partnerTypeFilter === "all"
                    ? "bg-primary text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                All ({facetCounts.total})
              </button>
              <button
                onClick={() => handleFilterChange("CardLinking")}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  partnerTypeFilter === "CardLinking"
                    ? "bg-primary text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <span className="flex items-center justify-center gap-1.5">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                  Shop via Card ({facetCounts.CardLinking})
                </span>
              </button>
              <button
                onClick={() => handleFilterChange("Affiliate")}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  partnerTypeFilter === "Affiliate"
                    ? "bg-primary text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <span className="flex items-center justify-center gap-1.5">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                  Shop via Link ({facetCounts.Affiliate})
                </span>
              </button>
            </div>
          </div>

          {/* Results container with infinite scroll */}
          <div
            ref={resultsContainerRef}
            className="flex-1 overflow-y-auto p-4 bg-gray-50"
          >
            {tabResults[partnerTypeFilter].map((result: any, index: number) => {
              const logoUrl = result.logo_url?.startsWith('//')
                ? `https:${result.logo_url}`
                : result.logo_url;
              const cashbackText = result.cashback_display || (result.cashback_value ? `${result.cashback_value}%` : null);
              const hasLocation = result.locations && result.locations.length > 0;
              const googleMapsUrl = hasLocation
                ? `https://www.google.com/maps?q=${result.locations[0][0]},${result.locations[0][1]}`
                : null;

              return (
                <div key={index} className="relative p-4 mb-3 bg-white rounded-lg shadow-sm border border-gray-200">
                  {/* Ellipsis button in top-right corner */}
                  <button
                    onClick={() => openJsonModal(result)}
                    className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 p-1 z-10"
                    title="View JSON"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                    </svg>
                  </button>

                  <div className="flex gap-4">
                    {/* Logo on the left */}
                    <div className="flex-shrink-0">
                      {logoUrl ? (
                        <img
                          src={logoUrl}
                          alt={result.business_name}
                          className="w-24 h-24 object-cover rounded-lg"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="w-24 h-24 bg-gray-200 rounded-lg flex items-center justify-center">
                          <span className="text-gray-400 text-xs">No logo</span>
                        </div>
                      )}

                      {/* Partner type and location icons below logo */}
                      <div className="mt-3 flex justify-center items-center gap-2">
                        {/* Partner type indicator */}
                        {result.partner_type && (result.partner_type === "CardLinking" || result.partner_type === "Affiliate") && (
                          <span className="inline-flex items-center gap-1 text-gray-500">
                            {result.partner_type === "CardLinking" ? (
                              <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                </svg>
                                <span className="text-xs">Card</span>
                              </>
                            ) : (
                              <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                </svg>
                                <span className="text-xs">Link</span>
                              </>
                            )}
                          </span>
                        )}
                        {/* Location pin */}
                        {googleMapsUrl && (
                          <a
                            href={googleMapsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-500 hover:text-primary transition-colors"
                            title="View on Google Maps"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </a>
                        )}
                      </div>
                    </div>

                    {/* Content on the right */}
                    <div className="flex-1 min-w-0">
                      {/* Business name and indicators */}
                      <div className="flex items-start justify-between gap-2 mb-1 pr-8">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-900 text-base">
                            {result.business_name}
                          </h3>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {result.is_featured && (
                            <span className="px-2 py-0.5 bg-primary text-white text-xs rounded-full">
                              Featured
                            </span>
                          )}
                          {result.is_new && (
                            <span className="px-2 py-0.5 bg-green-500 text-white text-xs rounded-full">
                              New
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Semantic summary */}
                      {result.semantic_summary && (
                        <div className="mb-2">
                          <p className={`text-sm text-gray-600 ${expandedSummaries.has(index) ? '' : 'line-clamp-2'}`}>
                            {result.semantic_summary}
                          </p>
                          {result.semantic_summary.length > 100 && (
                            <button
                              onClick={() => toggleSummaryExpansion(index)}
                              className="text-xs text-primary hover:underline mt-1"
                            >
                              {expandedSummaries.has(index) ? 'Show less' : 'Show more'}
                            </button>
                          )}
                        </div>
                      )}

                      {/* Cashback information */}
                      {cashbackText && (
                        <div className="mb-2">
                          <span className="inline-flex items-center px-2 py-1 bg-green-50 text-green-700 text-sm font-medium rounded">
                            💰 {cashbackText}
                          </span>
                        </div>
                      )}

                      {/* Brands as tags */}
                      {result.brands && result.brands.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2 items-center">
                          {[...result.brands]
                            .sort((a: string, b: string) => a.localeCompare(b))
                            .slice(0, expandedBrands.has(index) ? undefined : 10)
                            .map((brand: string, brandIndex: number) => (
                              <span
                                key={brandIndex}
                                className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded"
                              >
                                {brand}
                              </span>
                            ))}
                          {result.brands.length > 10 && (
                            <button
                              onClick={() => toggleBrandsExpansion(index)}
                              className="text-xs text-primary hover:underline"
                            >
                              {expandedBrands.has(index) ? 'Show less' : `Show ${result.brands.length - 10} more`}
                            </button>
                          )}
                        </div>
                      )}

                      {/* Featured text */}
                      {result.is_featured && result.featured_text && (
                        <div className="mt-2 p-2 bg-purple-50 rounded text-xs text-gray-700">
                          {result.featured_text}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {loading && (
              <div className="text-center py-4 text-gray-600">
                Loading...
              </div>
            )}
            {!loading && tabResults[partnerTypeFilter].length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No results found
              </div>
            )}
          </div>
        </div>
      </div>

      {/* JSON Modal */}
      <Modal
        isOpen={isJsonModalOpen}
        onRequestClose={closeJsonModal}
        className="bg-white rounded-lg shadow-xl max-w-4xl mx-auto my-8 p-6 outline-none max-h-[80vh] overflow-auto"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Search Result JSON</h2>
          <button
            onClick={closeJsonModal}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <pre className="bg-gray-50 p-4 rounded-lg overflow-auto text-sm font-mono">
          {JSON.stringify(selectedResultJson, null, 2)}
        </pre>
      </Modal>
    </div>
  );
};

export default AdminSearchPreview;
